import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { vaultAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { fmtDate } from '../utils/dateUtils';
import { parseApiError } from '../utils/errors';
import VaultRevealModal from '../components/VaultRevealModal';

/**
 * Everyone's view of the password bank: the credentials the super admin has
 * shared with this person. Passwords are never in the list payload — the only
 * way to see one is the reveal modal.
 */
export default function Vault() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin';

  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [configured, setConfigured] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revealing, setRevealing] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (search.trim()) params.search = search.trim();
    if (category) params.category = category;
    setLoading(true);
    vaultAPI.list(params)
      .then(({ data }) => setEntries(data))
      .catch((e) => setError(parseApiError(e, 'Failed to load the password bank')))
      .finally(() => setLoading(false));
  }, [search, category]);

  useEffect(() => {
    vaultAPI.status().then(({ data }) => setConfigured(data.configured)).catch(() => {});
    vaultAPI.categories().then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  // Debounce the search so typing does not fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="page">
      <div className="page-header">
        <h2>🔐 Password Bank</h2>
        {isAdmin && (
          <Link to="/admin/vault" className="btn btn-primary">Manage passwords</Link>
        )}
      </div>

      {!configured && (
        <div className="alert alert-warning">
          The password bank is not configured on this server yet. An administrator
          needs to set <code>VAULT_MASTER_KEY</code> and restart.
        </div>
      )}
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="filter-bar">
        <input
          type="search" value={search} placeholder="Search by name or username…"
          onChange={(e) => setSearch(e.target.value)} style={{ minWidth: 240 }}
        />
        <select value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">All categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="page-loading">Loading…</div>
      ) : entries.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 34, marginBottom: 8 }}>🔒</div>
          <p className="text-muted">
            {search || category
              ? 'No passwords match that filter.'
              : isAdmin
                ? 'No passwords stored yet. Use “Manage passwords” to add the first one.'
                : 'No passwords have been shared with you yet. The super admin grants access per credential.'}
          </p>
        </div>
      ) : (
        <div className="card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th><th>Category</th><th>Username / ID</th>
                <th>Link</th><th>Access until</th><th>Password</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id}>
                  <td>
                    <strong>{e.title}</strong>
                    {e.notes && (
                      <div className="text-muted" style={{ fontSize: 12, overflowWrap: 'anywhere' }}>{e.notes}</div>
                    )}
                  </td>
                  <td>{e.category ? <span className="badge badge-purple">{e.category}</span> : <span className="text-muted">—</span>}</td>
                  <td style={{ overflowWrap: 'anywhere' }}>{e.account_identifier || '—'}</td>
                  <td>
                    {e.url
                      ? <a href={e.url} target="_blank" rel="noopener noreferrer">Open</a>
                      : <span className="text-muted">—</span>}
                  </td>
                  <td>
                    {e.granted_expires_at
                      ? fmtDate(e.granted_expires_at, true)
                      : <span className="text-muted">{isAdmin ? 'Owner' : 'No expiry'}</span>}
                  </td>
                  <td>
                    <button className="btn btn-sm btn-primary" onClick={() => setRevealing(e)}>
                      👁 Reveal
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-muted" style={{ marginTop: 14, fontSize: 12.5 }}>
        Every reveal is recorded with your name, time and IP address. Do not
        share these credentials outside the company.
      </p>

      {revealing && (
        <VaultRevealModal entry={revealing} onClose={() => setRevealing(null)} />
      )}
    </div>
  );
}
